import { createAsyncThunk } from '@reduxjs/toolkit';

import { GeolocationService } from '@/shared/services/geolocationService';

import type {
  PortalAuthChannel,
  PortalConfirmContactByIdentifierPayload,
  PortalConfirmContactPayload,
  PortalConsumeMagicLinkFalha,
  PortalContactOtpResponse,
  PortalConversation,
  PortalFallbackResponse,
  PortalFaqItem,
  PortalKycInitiateResponse,
  PortalKycStatusResponse,
  PortalListFilters,
  PortalListResponse,
  PortalLoginMagicLinkPayload,
  PortalLoginMagicLinkResponse,
  PortalLoginRememberUsePayload,
  PortalLoginResponse,
  PortalLookupLoginPayload,
  PortalMagicLinkResponse,
  PortalMagicLinkStatusResponse,
  PortalManifestacaoPayload,
  PortalNotificadoDetail,
  PortalOnboardingPayload,
  PortalOtpPayload,
  PortalOtpResponse,
  PortalRememberHint,
  PortalRequestContactOtpByIdentifierPayload,
  PortalRequestContactOtpPayload,
  PortalTrackingEvent,
  PortalUploadResult,
  PortalValidateHashResponse,
} from '../types/portalTypes';
import type { AbaDoLink } from '../types/notificacaoDetalheTypes';
import type { TokenDeOrigem } from '../types/origemDoAcessoTypes';

import { ehLinkDeAcessoInvalido, mensagemSeguraPortal } from '../utils/portalError';
import { buildTrackingPayload } from '../utils/tracking';
import { portalService } from './services';

async function enrichTrackingEventWithGeolocation(
  event: PortalTrackingEvent,
): Promise<PortalTrackingEvent> {
  const location = await GeolocationService.getLocation();
  if (!location) {
    if (event.localizacao) {
      return event;
    }

    return {
      ...event,
      detalhes: {
        ...(event.detalhes ?? {}),
        geolocationFailed: true,
        nonCompliance:
          (event.detalhes as { nonCompliance?: string } | undefined)?.nonCompliance ??
          'GEOLOCATION_UNAVAILABLE',
      },
    };
  }

  return {
    ...event,
    localizacao: {
      latitude: location.lat,
      longitude: location.lon,
      accuracy: location.accuracy,
      provider: 'google_geolocation',
    },
  };
}

export const portalValidateHash = createAsyncThunk<
  PortalValidateHashResponse,
  { hash: string; sessaoId?: string },
  { rejectValue: string }
>('portal/validateHash', async ({ hash, sessaoId }, { rejectWithValue }) => {
  try {
    return await portalService.validateOnboardingHash(hash, sessaoId);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const portalRequestOnboardingOtp = createAsyncThunk<
  PortalOtpResponse,
  { hash: string; payload: PortalOtpPayload },
  { rejectValue: string }
>('portal/requestOnboardingOtp', async ({ hash, payload }, { rejectWithValue }) => {
  try {
    return await portalService.requestOnboardingOtp(hash, payload);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const portalActivateWithCode = createAsyncThunk<
  PortalLoginResponse,
  {
    hash: string;
    code: string;
    tracking?: Omit<PortalTrackingEvent, 'evento'>;
    origem?: TokenDeOrigem;
  },
  { rejectValue: string }
>('portal/activateWithCode', async ({ hash, code, tracking, origem }, { rejectWithValue }) => {
  try {
    return await portalService.activateWithCode(hash, code, tracking, origem);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const portalCompleteOnboarding = createAsyncThunk<
  PortalLoginResponse,
  { hash: string; payload: PortalOnboardingPayload; origem?: TokenDeOrigem },
  { rejectValue: string }
>('portal/completeOnboarding', async ({ hash, payload, origem }, { rejectWithValue }) => {
  try {
    return await portalService.completeOnboarding(hash, payload, origem);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const portalRequestLoginMagicLink = createAsyncThunk<
  PortalLoginMagicLinkResponse,
  PortalLoginMagicLinkPayload,
  { rejectValue: string }
>(
  'portal/requestLoginMagicLink',
  async ({ identifier, canalId, redirectTo }, { rejectWithValue }) => {
    try {
      return await portalService.requestLoginMagicLink(identifier, canalId, false, redirectTo);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

export const portalLookupLogin = createAsyncThunk<
  PortalLoginMagicLinkResponse,
  PortalLookupLoginPayload,
  { rejectValue: string }
>('portal/lookupLogin', async ({ identifier }, { rejectWithValue }) => {
  try {
    return await portalService.requestLoginMagicLink(identifier, null, true);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const portalSetLoginRemember = createAsyncThunk<
  { success: true },
  { identifier: string },
  { rejectValue: string }
>('portal/setLoginRemember', async ({ identifier }, { rejectWithValue }) => {
  try {
    return await portalService.setLoginRemember(identifier);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const portalGetLoginRememberHint = createAsyncThunk<
  PortalRememberHint | null,
  void,
  { rejectValue: string }
>('portal/getLoginRememberHint', async (_, { rejectWithValue }) => {
  try {
    return await portalService.getLoginRememberHint();
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const portalClearLoginRemember = createAsyncThunk<
  { success: true },
  void,
  { rejectValue: string }
>('portal/clearLoginRemember', async (_, { rejectWithValue }) => {
  try {
    return await portalService.clearLoginRemember();
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const portalUseLoginRemember = createAsyncThunk<
  PortalLoginMagicLinkResponse,
  PortalLoginRememberUsePayload,
  { rejectValue: string }
>('portal/useLoginRemember', async ({ canalId, redirectTo }, { rejectWithValue }) => {
  try {
    return await portalService.useLoginRemember(canalId, redirectTo);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const portalRequestContactOtp = createAsyncThunk<
  PortalContactOtpResponse,
  { hash: string; payload: PortalRequestContactOtpPayload },
  { rejectValue: string }
>('portal/requestContactOtp', async ({ hash, payload }, { rejectWithValue }) => {
  try {
    return await portalService.requestContactOtp(hash, payload);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const portalConfirmContact = createAsyncThunk<
  PortalContactOtpResponse,
  { hash: string; payload: PortalConfirmContactPayload },
  { rejectValue: string }
>('portal/confirmContact', async ({ hash, payload }, { rejectWithValue }) => {
  try {
    return await portalService.confirmContact(hash, payload);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const portalRequestContactOtpByIdentifier = createAsyncThunk<
  PortalContactOtpResponse,
  PortalRequestContactOtpByIdentifierPayload,
  { rejectValue: string }
>('portal/requestContactOtpByIdentifier', async (payload, { rejectWithValue }) => {
  try {
    return await portalService.requestContactOtpByIdentifier(payload);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const portalConfirmContactByIdentifier = createAsyncThunk<
  PortalContactOtpResponse,
  PortalConfirmContactByIdentifierPayload,
  { rejectValue: string }
>('portal/confirmContactByIdentifier', async (payload, { rejectWithValue }) => {
  try {
    return await portalService.confirmContactByIdentifier(payload);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const portalRequestMagicLink = createAsyncThunk<
  PortalMagicLinkResponse,
  { hash: string; canalId: string; aba?: AbaDoLink; origem?: TokenDeOrigem },
  { rejectValue: string }
>('portal/requestMagicLink', async ({ hash, canalId, aba, origem }, { rejectWithValue }) => {
  try {
    return await portalService.requestMagicLink(hash, canalId, aba, origem);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const portalConsumeMagicLink = createAsyncThunk<
  PortalLoginResponse,
  { token: string },
  { rejectValue: PortalConsumeMagicLinkFalha }
>('portal/consumeMagicLink', async ({ token }, { rejectWithValue }) => {
  try {
    return await portalService.consumeMagicLink(token);
  } catch (error) {
    return rejectWithValue({
      message: error instanceof Error ? error.message : 'Link de acesso inválido',
      linkInvalido: ehLinkDeAcessoInvalido(error),
    });
  }
});

export const portalRequestFallback = createAsyncThunk<
  PortalFallbackResponse,
  { hash: string; currentChannel: PortalAuthChannel; reason?: string },
  { rejectValue: string }
>('portal/requestFallback', async ({ hash, currentChannel, reason }, { rejectWithValue }) => {
  try {
    return await portalService.requestFallback(hash, currentChannel, reason);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const portalCheckMagicLinkStatus = createAsyncThunk<
  PortalMagicLinkStatusResponse,
  { hash: string },
  { rejectValue: string; state: { portal: { magicLinkPollToken: string | null } } }
>('portal/checkMagicLinkStatus', async ({ hash }, { rejectWithValue, getState }) => {
  try {
    const pollToken = getState().portal.magicLinkPollToken ?? undefined;
    return await portalService.checkMagicLinkStatus(hash, pollToken);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

// --- KYC ---

export const portalInitiateKyc = createAsyncThunk<
  PortalKycInitiateResponse,
  { hash: string },
  { rejectValue: string }
>('portal/initiateKyc', async ({ hash }, { rejectWithValue }) => {
  try {
    return await portalService.initiateKyc(hash);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const portalInitiateKycByIdentifier = createAsyncThunk<
  PortalKycInitiateResponse,
  { identifier: string },
  { rejectValue: string }
>('portal/initiateKycByIdentifier', async ({ identifier }, { rejectWithValue }) => {
  try {
    return await portalService.initiateKycByIdentifier(identifier);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const portalGetKycStatus = createAsyncThunk<
  PortalKycStatusResponse,
  { verificationId: string },
  { rejectValue: string }
>('portal/getKycStatus', async ({ verificationId }, { rejectWithValue }) => {
  try {
    return await portalService.getKycStatus(verificationId);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const portalLoginByHash = createAsyncThunk<
  PortalLoginResponse,
  { hash: string; sessaoId?: string; origem?: TokenDeOrigem },
  { rejectValue: string }
>('portal/loginByHash', async ({ hash, sessaoId, origem }, { rejectWithValue }) => {
  try {
    return await portalService.loginByHash(hash, sessaoId, origem);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const portalTrackEvent = createAsyncThunk<
  void,
  { itemId?: string; event: PortalTrackingEvent; includeGeolocation?: boolean },
  { rejectValue: string }
>(
  'portal/trackEvent',
  async ({ itemId, event, includeGeolocation = false }, { rejectWithValue }) => {
    try {
      const enrichedEvent = includeGeolocation
        ? await enrichTrackingEventWithGeolocation(event)
        : event;
      await portalService.trackEvent(itemId, enrichedEvent);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

export const portalListNotificacoes = createAsyncThunk<
  PortalListResponse,
  { filters: PortalListFilters; sessaoId?: string },
  { rejectValue: string }
>('portal/listNotificacoes', async ({ filters, sessaoId }, { rejectWithValue }) => {
  try {
    return await portalService.listNotificacoes(filters, sessaoId);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const portalGetDetail = createAsyncThunk<
  PortalNotificadoDetail,
  { id: string; sessaoId?: string; silencioso?: boolean },
  { rejectValue: string }
>('portal/getDetail', async ({ id, sessaoId }, { rejectWithValue }) => {
  try {
    const trackingPayload = await buildTrackingPayload(
      'DETAIL_REQUEST',
      sessaoId || '',
      undefined,
      true,
    );
    const enrichedPayload = await enrichTrackingEventWithGeolocation(trackingPayload);
    const { evento: _evento, ...trackingData } = enrichedPayload;

    return await portalService.getDetail(id, sessaoId, trackingData);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

const _portalTrackView = createAsyncThunk<
  void,
  { itemId: string; docId: string },
  { rejectValue: string }
>('portal/trackView', async ({ itemId, docId }, { rejectWithValue }) => {
  try {
    await portalService.trackView(itemId, docId);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const portalResponder = createAsyncThunk<
  unknown,
  { itemId: string; payload: PortalManifestacaoPayload },
  { rejectValue: string }
>('portal/responder', async ({ itemId, payload }, { rejectWithValue }) => {
  try {
    return await portalService.responder(itemId, payload);
  } catch (error) {
    return rejectWithValue(mensagemSeguraPortal(error));
  }
});

export const portalUploadAnexo = createAsyncThunk<
  PortalUploadResult,
  { itemId: string; file: File },
  { rejectValue: string }
>('portal/uploadAnexo', async ({ itemId, file }, { rejectWithValue }) => {
  try {
    return await portalService.uploadAnexo(itemId, file);
  } catch (error) {
    return rejectWithValue(mensagemSeguraPortal(error));
  }
});

export const portalDownloadDocumento = createAsyncThunk<string, string, { rejectValue: string }>(
  'portal/downloadDocumento',
  async (documentUrl, { rejectWithValue }) => {
    try {
      return await portalService.downloadDocumento(documentUrl);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

const PORTAL_DOC_PDF_POLL_INTERVAL_MS = 2000;
const PORTAL_DOC_PDF_MAX_ATTEMPTS = 60;

const esperarProximaConsultaDoPdf = (signal: AbortSignal): Promise<void> =>
  new Promise<void>((resolve) => {
    const aoCancelar = () => {
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', aoCancelar);
      resolve();
    }, PORTAL_DOC_PDF_POLL_INTERVAL_MS);
    signal.addEventListener('abort', aoCancelar, { once: true });
  });

const aguardarDocumentoPdfPronto = async (itemId: string, signal: AbortSignal): Promise<void> => {
  for (let attempt = 0; attempt < PORTAL_DOC_PDF_MAX_ATTEMPTS; attempt++) {
    if (signal.aborted) throw new Error('aborted');
    const status = await portalService.getDocumentPdfStatus(itemId);
    if (signal.aborted) throw new Error('aborted');
    if (status.status === 'READY') return;
    await esperarProximaConsultaDoPdf(signal);
  }
  throw new Error('Tempo esgotado aguardando geração do documento');
};

export const portalCarregarDocumentoPdf = createAsyncThunk<string, string, { rejectValue: string }>(
  'portal/carregarDocumentoPdf',
  async (itemId, { rejectWithValue, signal }) => {
    try {
      await aguardarDocumentoPdfPronto(itemId, signal);
      return portalService.getDocumentPdfContentUrl(itemId);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

// O download navega para uma signed URL, e não para a rota de stream que o visualizador
// usa: o navegador interno do WhatsApp no Android repassa o arquivo ao Chrome, que não
// tem o cookie da sessão do portal e recebia 401.
export const portalBaixarDocumentoPdf = createAsyncThunk<string, string, { rejectValue: string }>(
  'portal/baixarDocumentoPdf',
  async (itemId, { rejectWithValue, signal }) => {
    try {
      await aguardarDocumentoPdfPronto(itemId, signal);
      return await portalService.getDocumentPdfDownloadUrl(itemId);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

const _portalGetHelp = createAsyncThunk<
  PortalConversation,
  { itemId: string; sessaoId?: string },
  { rejectValue: string }
>('portal/getHelp', async ({ itemId, sessaoId }, { rejectWithValue }) => {
  try {
    return await portalService.getHelpConversation(itemId, sessaoId);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const portalLogout = createAsyncThunk<void, void, { rejectValue: string }>(
  'portal/logout',
  async (_, { rejectWithValue }) => {
    try {
      await portalService.logout();
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

export const fetchPortalFaq = createAsyncThunk<PortalFaqItem[]>('portal/fetchFaq', async () => {
  return await portalService.getFaq();
});
